import React, { useState } from "react";
import { api } from "../lib/api";
import Editable from "./Editable";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Instagram, UserCircle } from "lucide-react";

export const Contact = ({ data = {}, editable, onChange }) => {
  const setField = (k, v) => onChange && onChange({ ...data, [k]: v });
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.submitContact(form);
      toast.success("Thank you. Our team will revert within 24 hours.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-onyx-800 py-28 md:py-36" data-testid="contact-section">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:px-10 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-10 bg-gold" />
            <Editable
              editable={editable}
              value={data.eyebrow}
              onChange={(v) => setField("eyebrow", v)}
              className="font-cinzel text-[11px] tracking-[0.4em] uppercase text-gold"
              testId="contact-eyebrow"
            />
          </div>
          <Editable
            as="h2"
            editable={editable}
            value={data.title}
            onChange={(v) => setField("title", v)}
            className="font-display text-4xl md:text-5xl lg:text-6xl text-ivory leading-[1.05]"
            testId="contact-title"
          />

          <div className="mt-12 space-y-7">
            <div className="flex items-start gap-4">
              <UserCircle className="text-gold mt-1" size={20} />
              <div>
                <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-1">Director</p>
                <Editable
                  editable={editable}
                  value={data.director}
                  onChange={(v) => setField("director", v)}
                  className="text-ivory/85 brand-name text-base tracking-wider"
                  placeholder="Click to add director name…"
                  testId="contact-director"
                />
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="text-gold mt-1" size={20} />
              <div>
                <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-1">Studio</p>
                <Editable
                  editable={editable}
                  value={data.address}
                  onChange={(v) => setField("address", v)}
                  className="text-ivory/85"
                  testId="contact-address"
                />
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="text-gold mt-1" size={20} />
              <div>
                <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-1">Call</p>
                <Editable
                  editable={editable}
                  value={data.phone}
                  onChange={(v) => setField("phone", v)}
                  className="text-ivory/85"
                  testId="contact-phone"
                />
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="text-gold mt-1" size={20} />
              <div>
                <p className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-1">Email</p>
                <Editable
                  editable={editable}
                  value={data.email}
                  onChange={(v) => setField("email", v)}
                  className="text-ivory/85"
                  testId="contact-email"
                />
              </div>
            </div>
            <a
              href={data.instagram || "https://instagram.com/dhaddamotionpictures"}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 text-gold hover:text-gold-bright"
              data-testid="contact-instagram"
            >
              <Instagram size={18} />
              <span className="font-cinzel text-[11px] tracking-[0.3em] uppercase">
                {data.instagram_handle || "@dhaddamotionpictures"}
              </span>
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 gold-border bg-onyx-900 p-8 md:p-12" data-testid="contact-form">
          <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <label className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold/70">Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-cinema mt-2 w-full"
                placeholder="Your name"
                data-testid="contact-name"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold/70">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-cinema mt-2 w-full"
                placeholder="you@studio.com"
                data-testid="contact-email-input"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold/70">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="input-cinema mt-2 w-full"
                placeholder="+91"
                data-testid="contact-phone-input"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold/70">Project Type</label>
              <select
                value={form.project_type}
                onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                className="input-cinema mt-2 w-full bg-onyx-900"
                data-testid="contact-project-type"
              >
                <option value="">Select…</option>
                <option>Vanity Van Booking</option>
                <option>Music Video</option>
                <option>Feature Film</option>
                <option>Commercial / Ad</option>
                <option>Equipment Rental</option>
                <option>Location Scouting</option>
                <option>Other</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="font-cinzel text-[9px] tracking-[0.3em] uppercase text-gold/70">Message</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="input-cinema mt-2 w-full resize-none"
                placeholder="Tell us about your project, dates, and budget."
                data-testid="contact-message"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="mt-10 inline-flex items-center gap-3 bg-gold hover:bg-gold-bright disabled:opacity-50 px-8 py-4 font-cinzel text-[11px] tracking-[0.3em] uppercase text-onyx-900 transition-colors"
            data-testid="contact-submit"
          >
            <Send size={14} />
            {submitting ? "Sending…" : "Send Enquiry"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
